import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { uiAmountToLamports } from "@bridgesplit/utils";
import { SwapInstructionsResponse } from "@loopscale/pandora-types";

/**
 * Gets the actual output token from a swap quote, handling special cases like outputMintOverride
 * for async swaps (e.g., Fission swaps) where the actual output token may differ from the requested buy token.
 *
 * This handles edge cases where:
 * - The swap route returns a different output mint than requested (e.g., Fission swaps return redemption tokens)
 * - The quote's outputMint field may override the user's initial buyToken selection
 *
 * @param swapQuote - The swap instructions response from Pandora API
 * @param buyToken - The originally requested buy token
 * @param getMetadata - Function to resolve token metadata from mint address
 * @returns The actual output token that will be received, or the original buyToken if no override exists
 */
/** True when the quote was built for the current sell token and input amount (independent of output-mint overrides). */
export function isSwapQuoteForForm(
    swapQuote: SwapInstructionsResponse | undefined,
    sellToken: TokenListMetadata | undefined,
    inputAmount: number | undefined
): boolean {
    if (!swapQuote?.quote || !sellToken || inputAmount === undefined) return false;
    const q = swapQuote.quote;
    if (q.inputMint !== sellToken.mint) return false;
    return q.inputAmount === uiAmountToLamports(inputAmount, sellToken.decimals);
}

export function getActualBuyTokenFromSwapQuote(
    swapQuote: SwapInstructionsResponse | undefined,
    buyToken: TokenListMetadata | undefined,
    getMetadata: (mint: string) => TokenListMetadata | undefined
): TokenListMetadata | undefined {
    if (!buyToken) return undefined;

    const quoteOutputMint = swapQuote?.quote?.outputMint;

    // If the quote specifies a different output mint than the requested buy token,
    // use the quote's output mint (common for async swaps with intermediate tokens)
    if (quoteOutputMint && quoteOutputMint !== buyToken.mint) {
        return getMetadata(quoteOutputMint) || buyToken;
    }

    return buyToken;
}
